import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Text,
  Flex,
  useColorMode,
  useColorStyle,
  RadioGroup,
  Stack,
  Radio,
} from '@tonic-one/react';
import { useEffect, useRef, useState } from 'react';
import { AuthenticationInfoRes } from '@Types/provision';
import { ProvisionApi } from '@Apis';
import { useTranslation } from 'react-i18next';
import { ProvisionOrgInfo, StepIcon } from '../ProvisionModal/share';
import ErrorAlert from '@Components/Common/ErrorAlert';
import { reportPendo } from '../../../utils/setupPendo';
import { deleteCookie, getProvisionCookie } from '@Utils/common';
import CSpinner from '@Components/CSpinner';
import { ServiceForReAuth } from '@Types/administrator';

const StorageReauthModal = ({
  orgId,
  service,
  authAccount,
  onClose,
}: {
  orgId: string;
  service: ServiceForReAuth;
  authAccount: string;
  onClose: () => void;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation(['provision', 'common']);
  const [reAuthDone, setReAuthDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const timeRef = useRef<any>(null);
  const [useNewServiceAccount, setUseNewServiceAccount] = useState(0);

  const _service = service as
    | ServiceForReAuth.Box
    | ServiceForReAuth.Dropbox
    | ServiceForReAuth.GoogleDrive;
  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | AuthenticationInfoRes>(null);

  const cookieName = {
    [ServiceForReAuth.Box]: 'provision_status_box',
    [ServiceForReAuth.Dropbox]: 'provision_status_dropbox',
    [ServiceForReAuth.GoogleDrive]: 'provision_status_googledrive',
  }[_service];

  const onClick = async () => {
    const authUrl = authenticationInfo?.[0]?.authenticationUrl;
    if (!authUrl) {
      return;
    }

    reportPendo('ReAuth.GrantPermission.Click', {
      orgId,
      service,
    });
    setErrorMsg(undefined);
    setLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const provisionInfo = getProvisionCookie(cookieName);
      if (provisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code, state } = JSON.parse(provisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveReAuthInfoForStorage(
              _service,
              {
                authCode: code,
                csrfCode: state,
              },
              authAccount,
              orgId,
              !!useNewServiceAccount
            );
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision.service.${service}.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setReAuthDone(true);
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t(`provision.service.${service}.error.deniedGrant`));
        }
        setLoading(false);
      }
    }, 1000);
  };

  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  useEffect(() => {
    ProvisionApi.apiGetReAuthAuthenticationInfo(_service, orgId)
      .then((res) => {
        setAuthenticationInfo(res.data);
      })
      .catch((e) => {
        console.error(e);
        setAuthenticationInfo(null);
      });
  }, []);

  if (!authenticationInfo) {
    return <></>;
  }

  return (
    <>
      {loading && <CSpinner />}
      <Modal isOpen={true} isClosable onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t(`provision.reAuth.service.${service}.title`)}
          </ModalHeader>
          <ModalBody>
            <ProvisionOrgInfo orgId={orgId} />
            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <Flex gap="20px" my="10px">
              <StepIcon checked={reAuthDone} />
              <Flex direction="column" gap="12px">
                <Text size={16}>
                  {t('provision.reAuth.service.prepareServiceAccount')}
                </Text>
                <RadioGroup
                  value={useNewServiceAccount}
                  onChange={(value) => setUseNewServiceAccount(Number(value))}
                  disabled={reAuthDone}
                >
                  <Stack spacing="1x" shouldWrapChildren>
                    <Radio value={0}>
                      {t('provision.reAuth.service.useCurrentServiceAccount')} (
                      <Text display={'inline'} color={'#6F9BF4'}>
                        {authAccount}
                      </Text>
                      )
                    </Radio>
                    <Radio value={1}>
                      {t('provision.reAuth.service.useNewServiceAccount')}
                    </Radio>
                  </Stack>
                </RadioGroup>
                <Text pl={25} mt={-10} color={colorStyle.color.secondary}>
                  {t('provision.reAuth.service.useNewServiceAccountNote')}
                </Text>
              </Flex>
            </Flex>
            <Flex gap="20px" my="10px">
              <StepIcon checked={reAuthDone} />
              <Flex direction="column" gap="12px">
                <Text>
                  {t(`provision.reAuth.service.${service}.grantPermission`)}
                </Text>
                {reAuthDone ? (
                  <Text color={colorStyle.color.tertiary}>
                    {t('provision.common.permissionGranted')}
                  </Text>
                ) : (
                  <div>
                    <Button
                      variant="secondary"
                      borderRadius="2rem"
                      onClick={onClick}
                    >
                      {t('provision.button.grantPermission')}
                    </Button>
                  </div>
                )}
              </Flex>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button minWidth="20x" onClick={onClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default StorageReauthModal;
